// R77 — bevestigingsdialog wanneer geselecteerde Sikkens-orders al eerder geëxporteerd zijn.
import { useEffect, useRef } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  total: number;          // N — alle geselecteerde Sikkens-orders
  alreadyExported: number; // X
  onlyNewCount: number;   // M = N - X
  onExportNew: () => void;
  onExportAll: () => void;
}

export const ExportConfirmDialog = ({
  open,
  onOpenChange,
  total,
  alreadyExported,
  onlyNewCount,
  onExportNew,
  onExportAll,
}: Props) => {
  const newDisabled = onlyNewCount === 0;
  const primaryRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => primaryRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [open, newDisabled]);

  const tile3dBtn =
    "rounded-full relative overflow-hidden ring-1 ring-slate-300/60 " +
    "shadow-[0_3px_6px_rgba(0,0,0,0.18),inset_0_1px_0_rgba(255,255,255,0.6),inset_0_-1px_0_rgba(0,0,0,0.12)] " +
    "transition-all duration-150 " +
    "hover:-translate-y-0.5 hover:shadow-[0_5px_10px_rgba(0,0,0,0.22),inset_0_1px_0_rgba(255,255,255,0.65),inset_0_-1px_0_rgba(0,0,0,0.12)] " +
    "active:translate-y-0.5 active:shadow-[0_2px_4px_rgba(0,0,0,0.18),inset_0_1px_0_rgba(255,255,255,0.4)]";

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent
        className={cn(
          "p-7 rounded-xl border-border/60 ring-1 ring-border/50",
          "bg-gradient-to-b from-background to-muted/40",
          "shadow-[0_25px_50px_-12px_rgba(0,0,0,0.35),0_8px_16px_-8px_rgba(0,0,0,0.2),inset_0_1px_0_rgba(255,255,255,0.6)]",
          "before:pointer-events-none before:absolute before:inset-x-3 before:top-0 before:h-px before:bg-gradient-to-r before:from-transparent before:via-white/70 before:to-transparent",
        )}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>Welke orders exporteren?</AlertDialogTitle>
          <AlertDialogDescription>
            {alreadyExported} van de {total} geselecteerde orders {alreadyExported === 1 ? "is" : "zijn"} al eerder geëxporteerd.
            {newDisabled && (
              <span className="block mt-2 text-sm">
                Er zijn geen nieuwe orders om te exporteren — alle geselecteerde orders zijn al eerder geëxporteerd.
              </span>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className={tile3dBtn}>Annuleren</AlertDialogCancel>
          {newDisabled ? (
            <>
              <button
                type="button"
                disabled
                className={cn(
                  buttonVariants({ variant: "outline" }),
                  tile3dBtn,
                  "opacity-50 cursor-not-allowed hover:translate-y-0",
                )}
                title="Geen nieuwe orders in selectie"
              >
                Alleen nieuwe orders (0)
              </button>
              <AlertDialogAction ref={primaryRef} onClick={onExportAll} className={tile3dBtn}>
                Alles exporteren ({total})
              </AlertDialogAction>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={onExportAll}
                className={cn(buttonVariants({ variant: "outline" }), tile3dBtn)}
              >
                Alles exporteren ({total})
              </button>
              <AlertDialogAction ref={primaryRef} onClick={onExportNew} className={tile3dBtn}>
                Alleen nieuwe orders ({onlyNewCount})
              </AlertDialogAction>
            </>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ExportConfirmDialog;
